import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import { ImageList } from '@c/listing/components/ImageList';
import { AddPhotoIcon } from '@c/listing/icons/AddPhotoIcon';
import LoadingSpinner from '@c/LoadingSpinner';
import BaseModal from '@c/modals/BaseModal';
import Button from '@ui/Button';
import RadioButton from '@ui/RadioButton';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import { useReturns } from 'app/support/hooks/useReturns';
import { useToastContext } from 'context/ToastContext';
import { ChatDocument } from 'models/chat';
import { ReturnDocument } from 'models/returns';
import { useRef, useState } from 'react';

export const MarkReturnAsReceivedModal = ({
  isOpen,
  dismiss,
  returnDoc,
  onSuccess,
  forceNeedsAttention,
  chat,
}: {
  isOpen: boolean;
  dismiss: () => void;
  returnDoc: ReturnDocument;
  onSuccess?: () => void;
  forceNeedsAttention?: boolean;
  chat?: ChatDocument;
}) => {
  const [receivedForm, setReceivedForm] = useState({
    received_by_warehouse: forceNeedsAttention
      ? 'needs_attention'
      : returnDoc.received_by_warehouse ?? 'yes',
    warehouse_comments: returnDoc.warehouse_comments ?? '',
    images: returnDoc.images ?? [],
  });
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = `addPhotoInput-${returnDoc.id}`;
  const { showSuccessToast, showErrorToast } = useToastContext();
  const { markAsReceivedMutation } = useReturns({
    onMarkedAsReceived: (success) => {
      if (success) {
        dismiss();
        if (receivedForm.received_by_warehouse === 'needs_attention') {
          showSuccessToast(
            'Return issue reported. Our support team will contact you shortly'
          );
        } else {
          showSuccessToast('Return marked as received');
        }
        returnDoc.received_by_warehouse = 'yes';
        onSuccess?.();
      } else {
        showErrorToast('Failed to mark return as received');
      }
    },
  });
  const { uploadingImage, handleFileInputChange, deleteImage } =
    useMultiImageUpload({
      id: `${returnDoc.id}_${returnDoc.seller_id}`,
      type: 'other',
      onUploadCallback: ({ paths }) => {
        setReceivedForm({
          ...receivedForm,
          images: [...(receivedForm.images ?? []), ...paths],
        });
      },
      onDeleteCallback: (paths) => {
        setReceivedForm({ ...receivedForm, images: paths });
      },
    });
  return (
    <BaseModal isOpen={isOpen} dismiss={dismiss} title="Mark as Received">
      <div className="flex flex-col gap-4 p-8">
        {receivedForm.received_by_warehouse === 'needs_attention' && (
          <FormLabel value="Comments" className="text-[2rem]">
            <p className="my-4 text-[1.6rem] text-gray-500">
              {forceNeedsAttention
                ? 'Describe the issues below.'
                : 'If the item arrived in its original condition, please mark as received. If there are any issues, describe them below.'}
            </p>
            <FormTextarea
              value={receivedForm.warehouse_comments}
              onChange={(e) =>
                setReceivedForm({
                  ...receivedForm,
                  warehouse_comments: e.target.value,
                })
              }
              placeholder="Ex: Item came back damaged — scratches on visor and missing original packaging"
            />
          </FormLabel>
        )}

        {receivedForm.received_by_warehouse === 'needs_attention' && (
          <div className="flex w-full grow flex-col gap-4 px-4 ">
            <span className="font-semibold">Photos</span>
            <p className="text-[1.6rem] text-gray-500">
              Add supporting photos if the item arrived damaged or not in its
              original condition.
            </p>
            <button
              type="button"
              className={`h-[15rem] w-full cursor-pointer overflow-clip rounded-[1.6rem] outline-dashed outline-2 focus:outline-brand-secondary ${
                isDraggingFile
                  ? 'bg-brand-secondary/10 outline-brand-secondary'
                  : 'outline-brand-secondary-light hover:bg-brand-darker-white'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dragCounterRef.current += 1;
                if (
                  Array.from(e.dataTransfer.items ?? []).some(
                    (item) => item.kind === 'file'
                  )
                ) {
                  setIsDraggingFile(true);
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dragCounterRef.current = Math.max(
                  0,
                  dragCounterRef.current - 1
                );
                if (dragCounterRef.current === 0) setIsDraggingFile(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dragCounterRef.current = 0;
                setIsDraggingFile(false);
                const input = fileInputRef.current;
                if (!input || e.dataTransfer.files.length === 0) return;
                const dt = new DataTransfer();
                Array.from(e.dataTransfer.files).forEach((file) =>
                  dt.items.add(file)
                );
                input.files = dt.files;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }}
            >
              <label
                htmlFor={fileInputId}
                className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-[1.3rem]"
              >
                {uploadingImage ? (
                  <LoadingSpinner />
                ) : isDraggingFile ? (
                  <p className="text-[1.5rem] font-semibold text-brand-secondary">
                    Drop your file to upload
                  </p>
                ) : (
                  <>
                    <AddPhotoIcon />
                    <p className="hidden text-[1.3rem] font-semibold text-brand-gray sm:block">
                      Click or drop your photos here
                    </p>
                  </>
                )}
              </label>
              <input
                ref={fileInputRef}
                id={fileInputId}
                type="file"
                accept="image/*,video/*"
                multiple
                name="photo"
                className="hidden"
                onChange={handleFileInputChange}
              />
            </button>
            <ImageList images={receivedForm.images} deleteImage={deleteImage} />
          </div>
        )}
        {!forceNeedsAttention && (
          <>
            <RadioButton
              label="Item arrived in good condition"
              checked={receivedForm.received_by_warehouse === 'yes'}
              onClick={() => {
                setReceivedForm({
                  ...receivedForm,
                  received_by_warehouse: 'yes',
                });
              }}
            />
            <RadioButton
              label="Item arrived damaged or not in original condition"
              checked={receivedForm.received_by_warehouse === 'needs_attention'}
              onClick={() => {
                setReceivedForm({
                  ...receivedForm,
                  received_by_warehouse: 'needs_attention',
                });
              }}
            />
          </>
        )}

        <Button
          text={
            receivedForm.received_by_warehouse === 'needs_attention'
              ? 'Report Item Issue'
              : 'Mark as Received'
          }
          loading={markAsReceivedMutation.isPending}
          type="secondary"
          className="mt-6"
          onClick={() => {
            if (
              receivedForm.received_by_warehouse === 'needs_attention' &&
              !receivedForm.warehouse_comments
            ) {
              showErrorToast(
                'Please provide details about the condition of the item'
              );
              return;
            }
            markAsReceivedMutation.mutate({
              id: returnDoc.id,
              received_by_warehouse: receivedForm.received_by_warehouse,
              warehouse_comments: receivedForm.warehouse_comments,
              images: receivedForm.images,
              is_warehouse_return: returnDoc.is_warehouse_return,
              order_id: returnDoc.order_id,
              chat,
            });
          }}
        />
      </div>
    </BaseModal>
  );
};
